/**
 * reviewInvitationService.ts
 *
 * Magic-link review invitations (generalised 360Invite). All reviewer
 * paths use a single token-based flow — no login required.
 *
 * Reviewer identity is resolved from the M365-synced /users directory by
 * email. Reviewers without an account in the directory can still submit
 * (the token authorises the write).
 *
 * Collections:
 *   reviewInvitations  — one doc per (reviewFormId, reviewerEmail, role)
 *   reviewResponses    — one doc per submitted invitation (immutable)
 */

import {
  collection, doc, getDocs, getDoc, setDoc, addDoc, updateDoc,
  serverTimestamp, query, where, Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type {
  ReviewInvitation, ReviewResponse, ReviewResponseEntry, SelfNarrative,
  ReviewerRole, InvitationStatus,
} from '../types/magicLink';
import { REVIEWER_ATTRIBUTION } from '../types/magicLink';

const COL_INVITES   = 'reviewInvitations';
const COL_RESPONSES = 'reviewResponses';

function generateToken(): string {
  const arr = new Uint8Array(24);
  crypto.getRandomValues(arr);
  return Array.from(arr, b => b.toString(16).padStart(2, '0')).join('');
}

// ─── Create invitations ──────────────────────────────────────────────────────

export interface CreateInvitationInput {
  reviewFormId: string;
  cycleId: string;
  revieweeId: string;
  revieweeName: string;
  reviewerRole: ReviewerRole;
  reviewerEmail: string;
  reviewerId?: string;
  reviewerName?: string;
  reviewerWaNumber?: string;
  expiryDays?: number;       // default 14
}

/**
 * Create one invitation.
 *
 * SECURITY: token is used as the Firestore document ID. This means lookup
 * is a `get` (single doc by ID) instead of a `list` (query) — Firestore
 * rules can then permit `get: true` while denying `list` to non-admins.
 * Net effect: anyone with the token can load the invitation; nobody
 * (including authenticated users) can enumerate the collection.
 */
export async function createInvitation(
  input: CreateInvitationInput,
  baseUrl: string,
): Promise<{ id: string; token: string; url: string }> {
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + (input.expiryDays ?? 14));
  const token = generateToken();

  const docData = {
    reviewFormId:     input.reviewFormId,
    cycleId:          input.cycleId,
    revieweeId:       input.revieweeId,
    revieweeName:     input.revieweeName,
    reviewerRole:     input.reviewerRole,
    reviewerId:       input.reviewerId    ?? null,
    reviewerEmail:    input.reviewerEmail.toLowerCase().trim(),
    reviewerName:     input.reviewerName  ?? null,
    reviewerWaNumber: input.reviewerWaNumber ?? null,
    token,
    status:           'sent' as InvitationStatus,
    sentAt:           serverTimestamp(),
    expiresAt:        Timestamp.fromDate(expiresAt),
    remindersSent:    [],
  };

  await setDoc(doc(db, COL_INVITES, token), docData);
  return { id: token, token, url: `${baseUrl}/r/${token}` };
}

/** Batch create invitations for an entire reviewer panel. */
export async function createInvitationBatch(
  inputs: CreateInvitationInput[],
  baseUrl: string,
): Promise<Array<{ id: string; token: string; url: string; reviewerEmail: string; reviewerRole: ReviewerRole }>> {
  const results = await Promise.all(
    inputs.map(async (input) => {
      const r = await createInvitation(input, baseUrl);
      return { ...r, reviewerEmail: input.reviewerEmail, reviewerRole: input.reviewerRole };
    })
  );
  return results;
}

// ─── Lookup ──────────────────────────────────────────────────────────────────

/**
 * Look up invitation by token — direct GET (no query, no list permission needed).
 * Token is the document ID, so this works under `allow get: if true` rule.
 */
export async function getInvitationByToken(token: string): Promise<ReviewInvitation | null> {
  const snap = await getDoc(doc(db, COL_INVITES, token));
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as ReviewInvitation) : null;
}

export async function getInvitation(id: string): Promise<ReviewInvitation | null> {
  const snap = await getDoc(doc(db, COL_INVITES, id));
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as ReviewInvitation) : null;
}

export async function listInvitationsForForm(reviewFormId: string): Promise<ReviewInvitation[]> {
  const snap = await getDocs(
    query(collection(db, COL_INVITES), where('reviewFormId', '==', reviewFormId)),
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }) as ReviewInvitation);
}

export async function listInvitationsForCycle(cycleId: string): Promise<ReviewInvitation[]> {
  const snap = await getDocs(
    query(collection(db, COL_INVITES), where('cycleId', '==', cycleId)),
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }) as ReviewInvitation);
}

// ─── Self-mint (login-based /self-review) ───────────────────────────────────

/**
 * Get or self-mint a 'self' invitation for a logged-in user.
 * Token is stored on the reviewForm so subsequent visits find the same
 * invitation (idempotent). Random — not deterministic — to prevent
 * unauthenticated submission by guessing IDs.
 *
 * Firestore rules permit self-mint only when reviewerRole == 'self' AND
 * reviewerId == revieweeId == request.auth.uid (see reviewInvitations rules).
 */
export async function getOrCreateSelfInvitation(
  reviewForm: { id: string; cycleId: string; revieweeId: string; revieweeName: string; selfInvitationToken?: string },
  user: { id: string; name: string; email: string },
  baseUrl: string,
): Promise<ReviewInvitation> {
  if (reviewForm.selfInvitationToken) {
    const existing = await getInvitationByToken(reviewForm.selfInvitationToken);
    if (existing) return existing;
  }
  const { token } = await createInvitation({
    reviewFormId: reviewForm.id,
    cycleId:      reviewForm.cycleId,
    revieweeId:   reviewForm.revieweeId,
    revieweeName: reviewForm.revieweeName,
    reviewerRole: 'self',
    reviewerId:   user.id,
    reviewerEmail: user.email,
    reviewerName: user.name,
    expiryDays:   90,
  }, baseUrl);
  await updateDoc(doc(db, 'reviewForms', reviewForm.id), { selfInvitationToken: token });
  const minted = await getInvitationByToken(token);
  if (!minted) throw new Error('Failed to mint self-invitation');
  return minted;
}

// ─── Status updates ──────────────────────────────────────────────────────────

/** Mark invitation as opened (first time the reviewer clicks the link). */
export async function markInvitationOpened(id: string): Promise<void> {
  const inv = await getInvitation(id);
  if (!inv) return;
  if (inv.status !== 'sent') return; // only flip from 'sent' → 'opened'
  await updateDoc(doc(db, COL_INVITES, id), {
    status: 'opened',
    openedAt: serverTimestamp(),
  });
}

/** Mark invitation as in-progress (reviewer started filling). */
export async function markInvitationInProgress(id: string): Promise<void> {
  const inv = await getInvitation(id);
  if (!inv) return;
  if (inv.status === 'submitted' || inv.status === 'expired') return;
  await updateDoc(doc(db, COL_INVITES, id), { status: 'in_progress' });
}

// ─── Submit response ─────────────────────────────────────────────────────────

export interface SubmitResponseInput {
  invitation: ReviewInvitation;
  entries: ReviewResponseEntry[];
  selfNarrative?: SelfNarrative;
}

/**
 * Submit a review response. Token is invalidated on submit.
 * Anonymous responses (peer / direct_report) strip reviewerId before storage.
 */
export async function submitResponse(input: SubmitResponseInput): Promise<string> {
  const { invitation, entries, selfNarrative } = input;

  if (invitation.status === 'submitted') {
    throw new Error('This invitation has already been submitted.');
  }
  if (invitation.status === 'expired') {
    throw new Error('This invitation has expired.');
  }

  // No anonymous auth needed — rules allow create on reviewResponses if the
  // referenced invitation exists (token = doc ID, so attacker can't forge).
  const isAttributed = REVIEWER_ATTRIBUTION[invitation.reviewerRole] === 'attributed';

  const responseData: Record<string, unknown> = {
    invitationId:  invitation.id,
    reviewFormId:  invitation.reviewFormId,
    cycleId:       invitation.cycleId,
    revieweeId:    invitation.revieweeId,
    reviewerRole:  invitation.reviewerRole,
    entries,
    submittedAt:   serverTimestamp(),
    createdAt:     serverTimestamp(),
  };

  if (isAttributed) {
    if (invitation.reviewerId)    responseData.reviewerId    = invitation.reviewerId;
    if (invitation.reviewerEmail) responseData.reviewerEmail = invitation.reviewerEmail;
  }

  if (invitation.reviewerRole === 'self' && selfNarrative) {
    responseData.selfNarrative = selfNarrative;
  }

  const ref = await addDoc(collection(db, COL_RESPONSES), responseData);

  // Invalidate the invitation
  await updateDoc(doc(db, COL_INVITES, invitation.id), {
    status: 'submitted',
    submittedAt: serverTimestamp(),
  });

  return ref.id;
}

// ─── Fetch responses ─────────────────────────────────────────────────────────

/**
 * Fetch responses for a review form. By default returns admin-safe view
 * (anonymous responses have reviewerId/reviewerEmail stripped — already
 * stripped at write time). Pass `revealAll: true` only in HR/superadmin
 * surfaces with explicit need.
 */
export async function listResponsesForForm(reviewFormId: string): Promise<ReviewResponse[]> {
  const snap = await getDocs(
    query(collection(db, COL_RESPONSES), where('reviewFormId', '==', reviewFormId)),
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }) as ReviewResponse);
}

export async function listResponsesForCycle(cycleId: string): Promise<ReviewResponse[]> {
  const snap = await getDocs(
    query(collection(db, COL_RESPONSES), where('cycleId', '==', cycleId)),
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }) as ReviewResponse);
}

// ─── Submission stats ────────────────────────────────────────────────────────

export interface InvitationStats {
  total: number;
  sent: number;
  opened: number;
  inProgress: number;
  submitted: number;
  expired: number;
  submissionRate: number;
}

export function tallyInvitations(invitations: ReviewInvitation[]): InvitationStats {
  const stats: InvitationStats = {
    total: invitations.length,
    sent: 0, opened: 0, inProgress: 0, submitted: 0, expired: 0,
    submissionRate: 0,
  };
  for (const i of invitations) {
    switch (i.status) {
      case 'sent':        stats.sent++; break;
      case 'opened':      stats.opened++; break;
      case 'in_progress': stats.inProgress++; break;
      case 'submitted':   stats.submitted++; break;
      case 'expired':     stats.expired++; break;
    }
  }
  stats.submissionRate = stats.total ? stats.submitted / stats.total : 0;
  return stats;
}
